import style from './LuckyHomePage.css';
import IconButton from '../../components/shared/IconButton/IconButton';
import ArrowUpIcon from '../../components/shared/Icons/ArrowUpIcon';
import AddIcon from '../../components/shared/Icons/AddIcon';
import LuckyCardsListing from '../../components/LuckyHome/LuckyCardsListing/LuckyCardsListing';
import NavigationUtils from '../../utils/navigation.utils';
import useTranslation from '../../localization/i18n.context';
import { DeeplinkEvent } from '../../sdk/deeplink.event';
import { TrackingEvent } from '../../sdk/tracking.event';
import ArrowRightIcon from '../../components/shared/Icons/ArrowRightIcon';
import { renderIf } from '../../utils/render.utils';
import BottomSheet from '../../components/shared/BottomSheet/BottomSheet';
import List from '../../components/shared/List/List';
import { useState } from 'preact/hooks';
import CardStatus from '../../constants/card.status';
import useConfig from '../../config/config.context';
import InfoTriangle from '../../assets/images/info-triangle.webp';
import Popup from '../../components/shared/Popup/Popup';
import PosBottomSheet from '../../components/Pos/PosBottomSheet';
import SendMoneyOption from '../../components/SendMoney/SendMoneyOption';

export default function LuckyUserHasCardsView({
  cards = [],
  sendMoneyButtonEnabled = false,
  lendingButtonEnabled = false,
}) {
  const { t, getOrganization, getCurrency } = useTranslation();
  const [sendMoneyClicked, setSendMoneyClicked] = useState(false);
  const [sendMoneyToLockedCard, setSendMoneyToLockedCard] = useState(null);
  const activeCards = cards.filter(card =>
    [CardStatus.ACTIVE, CardStatus.LOCKED].includes(card.status),
  );
  const [posOptionsButtonEnabled, setPosOptionsButtonEnabled] = useState(false);
  const [showPosBottomSheet, setShowPosBottomSheet] = useState(false);
  const [card, setCard] = useState({});

  const { config } = useConfig();

  function onTopUpButtonClick() {
    let _topUpOptionsButtonEnabled = false;
    let _card = null;
    TrackingEvent.trigger(
      TrackingEvent.Events.DISCOVER_TAP_ACTION,
      TrackingEvent.Sources.CAAS,
      { source: 'top_up' },
    );

    if (cards.length > 1) {
      DeeplinkEvent.openWebview(
        `${NavigationUtils.Routes.TOPUP_CARD_LISTING}?source=${NavigationUtils.Sources.HOME}`,
      );
      return;
    }
    [_card] = cards;
    _topUpOptionsButtonEnabled = _card.posEnabled;
    if (_topUpOptionsButtonEnabled) {
      setShowPosBottomSheet(true);
    } else {
      DeeplinkEvent.openWebview(
        `${NavigationUtils.Routes.TOPUP}?organization=${_card.organizationCode}&source=${NavigationUtils.Sources.HOME}`,
      );
    }
    setPosOptionsButtonEnabled(_topUpOptionsButtonEnabled);
    setCard(_card);
  }

  function onSendMoneyClick() {
    // TODO: Define tracking later. Check the Top Up button to verify how to do it. Related ticket is https://lucky-app.atlassian.net/browse/CAAS-1658
    if (
      activeCards.length === 1 &&
      activeCards[0].status === CardStatus.ACTIVE &&
      !activeCards[0]?.sendMoneyCompanyEnabled
    ) {
      return DeeplinkEvent.openWebview(
        `${NavigationUtils.Routes.P2P_CONTACT_LIST.replace(
          ':cardUid',
          activeCards[0].uid,
        )}?source=${NavigationUtils.Sources.HOME}&organization=${
          activeCards[0].organizationCode
        }`,
      );
    }

    if (
      activeCards.length === 1 &&
      activeCards[0].status === CardStatus.LOCKED
    ) {
      return DeeplinkEvent.openWebview(
        `${NavigationUtils.Routes.CARD_SETTINGS}?source=${NavigationUtils.Sources.HOME}&organization=${activeCards[0].organizationCode}&cardUid=${activeCards[0].uid}`,
      );
    }

    setSendMoneyClicked(true);
  }

  return (
    <>
      <div className={style.actions}>
        <IconButton
          icon={<ArrowUpIcon />}
          text={t('Top up')}
          onClick={onTopUpButtonClick}
        />

        <IconButton
          icon={<ArrowRightIcon />}
          text={t('Send money')}
          disabled={!sendMoneyButtonEnabled}
          onClick={() => {
            onSendMoneyClick();
          }}
        />

        <IconButton
          icon={<AddIcon />}
          text={t('Add card')}
          type={IconButton.Type.SECONDARY}
          onClick={() => {
            TrackingEvent.trigger(
              TrackingEvent.Events.DISCOVER_TAP_ACTION,
              TrackingEvent.Sources.CAAS,
              { source: 'add_card' },
            );
            DeeplinkEvent.openWebview(
              `${NavigationUtils.Routes.ADD_CARD}?source=${NavigationUtils.Sources.HOME}`,
            );
          }}
        />
      </div>

      <LuckyCardsListing
        cards={cards}
        lendingButtonEnabled={lendingButtonEnabled}
      />

      {renderIf(
        sendMoneyClicked,
        <BottomSheet
          title={t('Select card to send money from')}
          onCloseClick={() => {
            setSendMoneyClicked(false);
          }}
        >
          <List data-testid="card-list">
            {activeCards.map(card => (
              <SendMoneyOption
                key={card.uid}
                card={card}
                t={t}
                config={config}
                getOrganization={getOrganization}
                getCurrency={getCurrency}
                onCardClick={(selectedCard, isLocked) => {
                  if (isLocked) {
                    setSendMoneyToLockedCard(selectedCard);
                    setSendMoneyClicked(false);
                  }

                  setSendMoneyClicked(false);
                }}
              />
            ))}
          </List>
        </BottomSheet>,
      )}

      <Popup
        title={t('Card Locked')}
        subtitle={t(
          "By locking your card, any card payment, transaction and ATM withdrawal won't work. You can unlock your card instantly anytime",
        )}
        icon={InfoTriangle}
        showing={sendMoneyToLockedCard !== null}
        primaryCta={{
          text: t('Unlock card'),
          onClick: () => {
            return DeeplinkEvent.openWebview(
              `${NavigationUtils.Routes.CARD_SETTINGS}?source=${NavigationUtils.Sources.HOME}&organization=${sendMoneyToLockedCard.organizationCode}&cardUid=${sendMoneyToLockedCard.uid}`,
            );
          },
        }}
        onCloseClick={() => {
          setSendMoneyClicked(true);
          setSendMoneyToLockedCard(null);
        }}
        data-testid="card-locked-pop-up"
      />
      {renderIf(
        showPosBottomSheet && posOptionsButtonEnabled,
        <PosBottomSheet
          show={showPosBottomSheet}
          isHome={true}
          organizationCode={card.organizationCode}
          cardStatus={card.status}
          cardUid={card.uid}
        ></PosBottomSheet>,
      )}
    </>
  );
}
